import { memo } from 'react';

type MessageSkeletonProps = {
  delay?: number;
};

const UserMessageSkeleton = ({ delay = 0 }: MessageSkeletonProps) => {
  return (
    <div
      className="flex flex-col items-end gap-2 px-16 py-5 duration-500 ease-out animate-in fade-in slide-in-from-bottom-2"
      style={{
        animationDelay: `${delay}ms`,
        animationFillMode: 'both',
      }}
    >
      {/* Message bubble skeleton */}
      <div className="relative flex w-96 flex-col items-start justify-center gap-2 rounded-2xl bg-primary px-5 py-4 pb-8 text-black shadow-sm shadow-black/10">
        {/* Text lines skeleton */}
        <div className="h-4 w-full animate-pulse rounded-sm bg-gray-300 dark:bg-gray-600" />
        <div className="h-4 w-5/6 animate-pulse rounded-sm bg-gray-300 dark:bg-gray-600" />
      </div>
    </div>
  );
};

const AgentMessageSkeleton = ({ delay = 0 }: MessageSkeletonProps) => {
  return (
    <div
      className="flex flex-col items-start gap-2 px-16 py-5 duration-500 ease-out animate-in fade-in slide-in-from-bottom-2"
      style={{
        animationDelay: `${delay}ms`,
        animationFillMode: 'both',
      }}
    >
      <div className="flex w-5/6 flex-col gap-2 rounded-xl px-8">
        <div className="space-y-2">
          {/* Text lines skeleton */}
          <div className="h-4 animate-pulse rounded-sm bg-gray-300 dark:bg-gray-600" />
          <div className="h-4 w-3/4 animate-pulse rounded-sm bg-gray-300 dark:bg-gray-600" />
          <div className="h-4 w-1/2 animate-pulse rounded-sm bg-gray-300 dark:bg-gray-600" />
        </div>
      </div>
    </div>
  );
};

export const ConversationLoading = memo(() => {
  const skeletonMessages = Array(5).fill(null);

  return (
    <div className="size-full overflow-hidden pb-28 [scrollbar-gutter:stable]">
      {/* eslint-disable react/no-array-index-key */}
      {skeletonMessages.map((_, index) => {
        const delay = index * 200;

        return (
          <div
            key={index}
            className="duration-500 ease-out animate-in fade-in slide-in-from-bottom-2"
            style={{
              animationDelay: `${delay}ms`,
              animationFillMode: 'both',
            }}
          >
            {index % 3 === 0 ? <UserMessageSkeleton delay={delay} /> : <AgentMessageSkeleton delay={delay} />}
          </div>
        );
      })}
      {/* eslint-enable react/no-array-index-key */}
    </div>
  );
});

ConversationLoading.displayName = 'ConversationLoading';
